import { ApolloError, gql, MutationFunctionOptions, useMutation } from '@apollo/client'
import { navigate } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import { clock } from '../../../../common/src/clock'
import Button from '../../../../common/src/components/Button'
import Icon from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import {
  Spacer,
  EllipsisDiv,
  BodyText,
  SmallerMonoText,
  BodyTextSpan,
  IntroText,
  SmallerText,
} from '../../../../common/src/components/core'
import { ColorName, Colors } from '../../../../common/src/constants'
import { appContext } from '../../../../common/src/context'
import { handleError } from '../../../../common/src/data/errors'
import misspellings from '../../../../common/src/data/misspellings.json'
import { addFeedback } from '../../../../common/src/feedback'
import { formatDuration, formatEnum } from '../../../../common/src/format'
import { randomInt } from '../../../../common/src/func'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import { CancelablePromise, makeCancelable } from '../../../../common/src/promise'
import {
  FetchInboxQuery,
  FetchTasksPageQuery,
  QuickActionResult,
  SendMessageMutation,
  SendMessageMutationVariables,
  MessageType,
  ConversationType,
  ThreadBotStatus,
  SendQueue,
  ActionToExecFragment,
  ActionInput,
  ActionName,
  FetchUserContextQuery,
  FetchThreadQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import { postActionUncaught } from '../../graphql/mutations'
import TextAreaAutosize from '../components/TextAreaAutosize'
import { getHash } from '../route'
import QuickActionComposer from './QuickActionComposer'
import QuickActionsBox from './QuickActionsBox'
import { computeWarnings, Warning } from './sendMessageWarnings'

const sendMessageMutation = gql`
  mutation SendMessage(
    $threadId: Int!
    $body: String!
    $emailSubject: String
    $mediaUrl: String
    $isInbound: Boolean
    $replyToMessageId: Int
    $operatorResponseTimeMs: Int
    $actionCount: Int
  ) {
    sendMessage(
      message: {
        threadId: $threadId
        body: $body
        emailSubject: $emailSubject
        replyToMessageId: $replyToMessageId
        mediaUrl: $mediaUrl
        isInbound: $isInbound
      }
      operatorResponseTimeMs: $operatorResponseTimeMs
      actionCount: $actionCount
    ) {
      ...ThreadMessages
    }
  }
  ${Fragments.threadMessages}
  ${Fragments.message}
  ${Fragments.messagesWithLinks}
  ${Fragments.contact}
  ${Fragments.botThread}
  ${Fragments.actionToExec}
`

const receiveCallMutation = gql`
  mutation ReceiveCall($threadId: Int!) {
    receiveCall(threadId: $threadId) {
      ...ThreadMessages
    }
  }
  ${Fragments.threadMessages}
  ${Fragments.message}
  ${Fragments.messagesWithLinks}
  ${Fragments.contact}
  ${Fragments.botThread}
  ${Fragments.actionToExec}
`

interface SendMessageBoxProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>
  quickactionRef: React.RefObject<HTMLInputElement>
  isTyping: string[]
  user: FetchUserContextQuery['self']
  thread: FetchThreadQuery['thread']
  botThread: FetchThreadQuery['thread']['botThread'] | null
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['thread']>['leasingTask']
  replyingToMessage?: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  resetReplyTo: () => void
  onArchiveThread: () => void
  maybeClearSelection?: () => void
  timeThreadFirstSeen?: Date
  staleThread: boolean
  setStaleThread: (stale: boolean) => void
  onSubmit: () => void
  actionCount: number
}

interface SendMessageBoxState {
  /** The current time, used for showing countdowns. */
  timeNow: string

  /** The text in the send box. */
  textEntered: string

  /** Warning, yellow text to alert the operator of unusual threads. Bot stopped, contact blocked, english only, etc. */
  warning?: string

  /** Active quick action, if any. This prefills the send box. */
  activeQA?: QuickActionResult

  /** Active quick action composer UI, if any, eg Cross Sell Search */
  qaComposerJson?: string

  /** Whether we're in the middle of executing an action. This disables all buttons until the action either succeeds or fails. */
  submitting: boolean
}

type SendMessageBoxImplProps = SendMessageBoxProps & {
  mutate: (options?: MutationFunctionOptions<SendMessageMutation, SendMessageMutationVariables>) => Promise<any>
}

export class SendMessageBoxImpl extends React.PureComponent<SendMessageBoxImplProps, SendMessageBoxState> {
  state: SendMessageBoxState = {
    timeNow: clock.now(),
    textEntered: '',
    warning: undefined,
    activeQA: undefined as QuickActionResult | undefined,
    submitting: false,
    qaComposerJson: undefined,
  }

  CREATE_NOTE_VALIDATION_ERROR_MESSAGE = 'Wrap your new note in quotes, “like this”'

  cancelablePromise: CancelablePromise<any> | undefined

  componentDidMount() {
    clock.addListener(this.onClockBound)
    keyboard.addListener(this.onKeyboardBound)
  }

  componentDidUpdate(prevProps: SendMessageBoxImplProps) {
    if (
      prevProps.thread.id !== this.props.thread.id ||
      prevProps.thread.conversationType !== this.props.thread.conversationType ||
      this.props.staleThread
    ) {
      // clear active quick action
      this.setState({ textEntered: '', activeQA: undefined, warning: undefined })
      this.props.setStaleThread(false)
    }
  }

  componentWillUnmount() {
    if (this.cancelablePromise) {
      this.cancelablePromise.cancel()
    }
    clock.removeListener(this.onClockBound)
    keyboard.removeListener(this.onKeyboardBound)
  }

  onResetReplyToClicked = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    this.props.resetReplyTo()
    e.stopPropagation()
  }

  /**
   * jumps you to the message you're replying to when you click
   */
  onReplyingToClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (this.props.replyingToMessage) {
      navigate(getHash('message', this.props.replyingToMessage.id), { replace: true }).catch(handleError)
      e.stopPropagation()
    }
  }

  /**
   * This part emulates the the context you get in something like an email client or messenger.
   */
  renderReplyingToMessageContext() {
    if (this.props.replyingToMessage && this.props.replyingToMessage.messageType === MessageType.EMAIL) {
      return (
        <OriginalMessage onClick={this.onReplyingToClick}>
          <OriginalMessageText color="silver">
            <ReplyingTo>Replying to: </ReplyingTo>
            {this.props.replyingToMessage.censoredShortBody}
          </OriginalMessageText>

          <ResetReplyTo color="silver" onClick={this.onResetReplyToClicked}>
            <Icon name="close" />
          </ResetReplyTo>
        </OriginalMessage>
      )
    }

    return null
  }

  private getSendType(messageType: MessageType) {
    return messageType === 'EMAIL' ? 'email' : 'SMS'
  }

  private getWarnings(
    task:
      | NonNullable<FetchInboxQuery['thread']>['leasingTask']
      | NonNullable<FetchTasksPageQuery['thread']>['leasingTask'],
    thread: FetchThreadQuery['thread'],
    conversationType: ConversationType,
    preferMessageType: MessageType,
    llmAction?: boolean | null
  ): Warning[] {
    const warnings = computeWarnings(task, thread, conversationType, preferMessageType, llmAction || false)
    const { warning } = this.state
    if (warning) {
      warnings.push({ text: warning, color: 'coral' })
    }
    return warnings
  }

  render() {
    const { textEntered, activeQA, qaComposerJson } = this.state
    const { thread, botThread, task, isTyping } = this.props

    if (!thread || !task) {
      return null
    }

    const botAction = (botThread && botThread.action) || undefined
    const botPreview = botAction && botAction.preview
    const botSendDate = botThread && botThread.showCountdown ? new Date(botThread.sendTime!) : undefined

    const { conversationType } = thread
    const { preferMessageType } = thread.contactThem
    const warnings = this.getWarnings(
      task,
      thread,
      conversationType,
      preferMessageType,
      botAction ? botAction.llmAction : null
    )
    const disableSend = warnings.some(w => w.blockSend)

    const activeAction = (activeQA && activeQA.action) || undefined

    return (
      <SendContainer key="reply">
        {qaComposerJson && (
          <QuickActionComposer
            composerJson={qaComposerJson}
            onChoose={this.finishedComposingQa}
            onCancel={this.cancelComposer}
          />
        )}
        <SendForm onSubmit={this.onSubmitBound}>
          {warnings.length > 0 && this.renderWarnings(warnings)}
          {!activeAction && (
            <QuickActionsBox
              innerRef={this.props.quickactionRef}
              key={'search-' + thread.id}
              threadId={thread.id}
              onChoose={this.chooseQA}
            />
          )}
          {/* eslint-disable-next-line react/jsx-no-bind */}
          {activeAction && <ActiveQADisplay onCancel={() => void this.cancelQA()} qa={activeQA!} />}
          <Spacer height={8} />
          {this.renderReplyingToMessageContext()}
          <TextAreaAutosize
            innerRef={this.props.textareaRef}
            id={'message-input'}
            value={textEntered}
            onClick={this.maybePrefillResponse}
            onChange={this.changeText}
            placeholder={this.maybePrefillPlaceholder(botPreview)}
            disabled={disableSend}
          />
          {this.renderTypingIndicator(isTyping)}
          <Spacer height={16} />
          <SendButtonRow>
            {this.renderCountdownLinks()}
            {this.renderSendButtons(disableSend, activeAction, botAction, botSendDate)}
            {this.maybeRenderDebugReceiveButtons(disableSend)}
          </SendButtonRow>
        </SendForm>
        <Spacer height={64} />
        <Spacer height={16} />
      </SendContainer>
    )
  }

  private renderCountdownLinks(): React.ReactNode {
    const { user, thread, task, botThread } = this.props
    const { contactThem } = thread

    // Does the bot want to do anything?
    const botAction = botThread && botThread.action

    const botSendDate = botThread && botThread.showCountdown ? new Date(botThread.sendTime!) : undefined
    const botClassification = botAction && botAction.classification

    let botSendStatus = ''
    let botSendColor: ColorName = 'sky'
    if (thread.botStatus !== ThreadBotStatus.ACTIVE) {
      botSendStatus =
        thread.botStatus === ThreadBotStatus.STOPPED ? 'Bot stopped' : 'Contact blocked by ' + task.firm.firmName
      botSendColor = 'coral'
    } else if (task.isBotDisabled) {
      botSendStatus = 'Bot disabled for task'
      botSendColor = 'coral'
    } else if (botSendDate != null) {
      const millisUntil = botSendDate.getTime() - new Date(this.state.timeNow).getTime()
      if (millisUntil < 0) {
        botSendStatus = 'Sending any second now'
      } else {
        botSendStatus = 'Sending in ' + formatDuration(millisUntil)
      }
    }

    const showBotAction = user.permissions.canViewBotAction

    return (
      <SentButtonRowDetails>
        <EllipsisDiv color={botSendColor}>{botSendStatus}</EllipsisDiv>
        {showBotAction && this.renderBotActionLink(task, contactThem, botAction, botClassification)}
      </SentButtonRowDetails>
    )
  }

  private renderSendButtons(
    disableSend: boolean,
    activeAction?: QuickActionResult['action'],
    botAction?: NonNullable<FetchThreadQuery['thread']['botThread']>['action'],
    botSendDate?: Date
  ): React.ReactNode {
    const { textEntered, submitting } = this.state
    const { thread } = this.props

    // Is the user planning to override the bot?
    const isTextEmpty = textEntered.trim() === ''

    // Everyone: Send SMS, Send Email, Send Now, Do without sending buttons
    const sendType = this.getSendType(thread.messageType)
    const isBotActionSched = botAction && botAction.sendQueue === SendQueue.SCHEDULED

    const showDoWithoutSending = activeAction && isTextEmpty
    const showSendNow = !showDoWithoutSending && botSendDate && isTextEmpty && !isBotActionSched
    const showSendSmsEmail = !showDoWithoutSending && !showSendNow

    return (
      <>
        {showDoWithoutSending && this.renderDoWithoutSendingButton(submitting, activeAction)}
        {showSendNow && this.renderSendNowButton(submitting, botAction)}
        {showSendSmsEmail &&
          this.renderSendSmsEmailButton(sendType, disableSend || isTextEmpty || submitting, activeAction)}
      </>
    )
  }

  /** Receive SMS, etc test buttons */
  private maybeRenderDebugReceiveButtons(disableSend: boolean) {
    const { user, thread } = this.props
    const { textEntered, submitting } = this.state
    const showReceiveButton = !appContext().isProduction || user.id === 11 || user.id === 51
    if (!showReceiveButton) {
      return null
    }
    const isTextEmpty = textEntered.trim() === ''
    const isTextUrl = textEntered.trim().startsWith('https://')

    const showReceiveCall = isTextEmpty && thread.messageType === 'SMS'
    const showReceiveSmsEmail = !showReceiveCall && !isTextUrl
    const showReceiveMms = !showReceiveCall && isTextUrl

    const sendType = this.getSendType(thread.messageType)

    return (
      <>
        {showReceiveSmsEmail && this.renderRecvSmsEmailButton(sendType, disableSend || isTextEmpty || submitting)}
        {showReceiveMms && this.renderRecvMmsButton(disableSend || submitting)}
        {showReceiveCall && this.renderRecvCallButton(submitting)}
      </>
    )
  }

  private renderRecvCallButton(submitting: boolean): React.ReactNode {
    return (
      <SendButton onClick={this.debugReceiveCallBound} disabled={submitting}>
        Receive Call
      </SendButton>
    )
  }

  private renderDoWithoutSendingButton(submitting: boolean, action?: ActionToExecFragment | null): React.ReactNode {
    return (
      <SendButton onClick={this.sendOrExecuteBound} disabled={submitting} action={action} hideArchiveThread={true}>
        Do without sending
      </SendButton>
    )
  }

  private renderSendNowButton(submitting: boolean, action?: ActionToExecFragment | null): React.ReactNode {
    return (
      <SendButton onClick={this.sendNowBound} disabled={submitting} action={action}>
        Send now
      </SendButton>
    )
  }

  private renderSendSmsEmailButton(
    sendType: string,
    disabled: boolean,
    action?: ActionToExecFragment | null
  ): React.ReactNode {
    return (
      <SendButton onClick={this.sendOrExecuteBound} disabled={disabled} action={action}>
        Send {sendType}
      </SendButton>
    )
  }

  private renderRecvSmsEmailButton(sendType: string, disabled: boolean): React.ReactNode {
    return (
      <SendButton onClick={this.debugReceiveMessageBound} disabled={disabled}>
        Receive {sendType}
      </SendButton>
    )
  }

  private renderRecvMmsButton(disabled: boolean): React.ReactNode {
    return (
      <SendButton onClick={this.debugReceiveMmsBound} disabled={disabled}>
        Receive MMS
      </SendButton>
    )
  }

  private renderTypingIndicator(isTyping: string[]): React.ReactNode {
    return (
      <TypingIndicatorText color="silver">
        {isTyping.length >= 3 && 'Several people are typing…'}
        {isTyping.length > 0 && isTyping.length < 3 && (
          <span>
            {isTyping.map((name, i) => (
              <span key={i}>
                <strong>{name}</strong>
                {i < isTyping.length - 1 && ' and '}
              </span>
            ))}
            {isTyping.length === 1 ? ' is typing…' : ' are typing…'}
          </span>
        )}
      </TypingIndicatorText>
    )
  }

  private renderWarnings(warnings: Warning[]): React.ReactNodeArray {
    return warnings.map((w, i) => (
      <React.Fragment key={i}>
        <BodyText color={w.color}>
          {w.link ? (
            <a href={w.link} style={{ color: w.color, textDecoration: 'none' }}>
              {w.text}
            </a>
          ) : (
            w.text
          )}
        </BodyText>
        <br />
      </React.Fragment>
    ))
  }

  private renderBotActionLink(
    task:
      | NonNullable<FetchInboxQuery['thread']>['leasingTask']
      | NonNullable<FetchTasksPageQuery['thread']>['leasingTask'],
    contactThem: FetchThreadQuery['thread']['contactThem'],
    botAction: NonNullable<FetchThreadQuery['thread']['botThread']>['action'] | null,
    botClassification: string | null | undefined
  ): React.ReactNode {
    return (
      <SmallerMonoText style={this.styleActionLinkMaxWidth}>
        <Link color="midway" href={`/leasing/data/botthread?taskId=${task.id}&contactId=${contactThem.id}`} noTab>
          {botAction != null ? botClassification || 'No classification' : 'Bot has nothing to say'}
        </Link>
      </SmallerMonoText>
    )
  }

  /**
   * Does one of three things:
   * - If we just a message in the compose box, sends the message
   * - If we have an active action and a message in the compose box, execute the action, replacing the message with our own
   * - If we have an active action and no message (empty compose box), execute the action, removing the message ("do without sending")
   */
  private sendOrExecuteBound = () => {
    const { textEntered, activeQA } = this.state
    const message = textEntered.trim()

    let promise: Promise<any> | undefined
    if (this.checkWarningBeforeSend()) {
      // short-circuit (displays warning before sending)
      return
    } else if (activeQA && activeQA.action) {
      promise = this.executeAction(activeQA.action)
    } else {
      promise = this.sendOrReceiveMessage(false)
    }

    this.props.resetReplyTo()
    this.submit(promise, message === '' ? 'Done' : 'Sent')
  }

  /**
   * Executes an action which must not contain any message. For example, Archive Thread.
   *
   * Prompts the user first to confirm.
   */
  private confirmPostActionBound = (prompt: string, action: ActionToExecFragment) => {
    if (!prompt.includes('speaks') && !confirm(prompt)) {
      return false
    }

    // Just execute the action without _submit.
    // This function runs in background after confirm dialog, disabling buttons does not make sense.
    this.executeAction(action).catch(handleError)

    return true
  }

  /**
   * Sends the current bot action (countdown) immediately.
   */
  private sendNowBound = () => {
    if (!this.props.botThread || !this.props.botThread.action) {
      return
    }
    const { action } = this.props.botThread
    this.submit(this.executeActionNow(action), 'Sent')
  }

  private debugReceiveMessageBound = () => {
    this.submit(this.sendOrReceiveMessage(true), 'Received')
  }

  private debugReceiveMmsBound = () => {
    this.submit(this.sendOrReceiveMessage(true, true), 'Received')
  }

  private debugReceiveCallBound = () => {
    const { thread } = this.props

    const promise = apolloWebClient.mutate({
      mutation: receiveCallMutation,
      variables: { threadId: thread.id },
    })

    this.submit(promise, 'Received')
  }

  private executeAction(action: ActionToExecFragment, sendNow?: boolean) {
    const {
      thread,
      onArchiveThread,
      replyingToMessage,
      maybeClearSelection,
      timeThreadFirstSeen,
      onSubmit,
      actionCount,
    } = this.props
    const { activeQA, textEntered } = this.state
    const message = textEntered.trim()

    let editedBody
    if (activeQA && activeQA.action && activeQA.body && activeQA.body !== message) {
      editedBody = message
    }

    const isTextEmpty = textEntered.trim() === ''
    const hasArchiveSideEffect = !isTextEmpty && (action.hasArchiveCommand || action.hasSnoozeCommand)

    // optimistic archive
    if (hasArchiveSideEffect) {
      onArchiveThread()
    }

    const operatorResponseTimeMs = timeThreadFirstSeen ? new Date().getTime() - timeThreadFirstSeen.getTime() : null

    // for each action, log how long Operator took + the displayName (human-friendly) of the QA they used
    const operatorMetadata = {
      operatorResponseTimeMs,
      qaDisplayName: activeQA?.displayName,
    }

    // ... but don't lose any metadata that the QA already tries to pass
    const logSummaryMerged = {
      ...(JSON.parse(action.logSummary) || {}),
      ...operatorMetadata,
    }

    const actionInput: ActionInput = {
      actionName: action.actionName,
      commandJsons: action.commandJsons,
      sendNow,
      editedBody,
      uuid: action.uuid,
      logSummary: JSON.stringify(logSummaryMerged),
      replyToMessageId: replyingToMessage && replyingToMessage.id,
      llmAction: action.llmAction,
    }

    onSubmit()

    return postActionUncaught({
      threadId: thread.id,
      action: actionInput,
      actionCount,
    })
      .then(() => {
        // TODO: reset the operatorResponseTimeMs counter so that successive actions don't overcount time-on-thread
        if (hasArchiveSideEffect && maybeClearSelection) {
          maybeClearSelection()
        }
      })
      .catch(handleError)
  }

  private executeActionNow(action: ActionToExecFragment) {
    return this.executeAction(action, true)
  }

  private sendOrReceiveMessage(isInbound: boolean, isMms?: boolean): Promise<any> {
    const { textEntered } = this.state
    const { thread, timeThreadFirstSeen, onSubmit, actionCount } = this.props

    const operatorResponseTimeMs = timeThreadFirstSeen ? new Date().getTime() - timeThreadFirstSeen.getTime() : null

    const variables: SendMessageMutationVariables = {
      threadId: thread.id,
      body: isMms ? '' : textEntered,
      mediaUrl: isMms ? textEntered : null,
      isInbound,
      replyToMessageId: this.props.replyingToMessage && this.props.replyingToMessage.id,
      operatorResponseTimeMs,
      actionCount,
    }

    console.log('Send Message', variables)

    onSubmit()

    return this.props.mutate({ variables })
  }

  private submit(promise: Promise<any>, feedbackMessage: string) {
    this.setState({ submitting: true })
    this.cancelablePromise = makeCancelable(promise)

    this.cancelablePromise.promise
      .then(() => {
        // TODO: reset the operatorResponseTimeMs counter so that successive actions don't overcount time-on-thread
        addFeedback(feedbackMessage)
        this.setState({ textEntered: '', activeQA: undefined, warning: undefined, submitting: false })
      })
      .catch((e: ApolloError | { canceled: boolean }) => {
        if ('canceled' in e && e.canceled) {
          return
        }
        this.setState({ submitting: false })
        handleError(e as ApolloError)
      })
  }

  private styleActionLinkMaxWidth = { maxWidth: '100%' }

  private onClockBound = (timeNow: string) => this.setState({ timeNow })

  private onKeyboardBound = (action: KeyAction) => {
    if (action === KeyAction.message && this.props.textareaRef.current) {
      this.props.textareaRef.current.focus()
    } else if (action === KeyAction.editMessage && this.props.textareaRef.current) {
      this.maybePrefillResponse()
      this.props.textareaRef.current.focus()
    } else if (action === KeyAction.quickAction) {
      if (this.props.quickactionRef.current) {
        this.props.quickactionRef.current.focus()
      }
    } else if (action === KeyAction.esc) {
      if (this.props.textareaRef.current) {
        this.props.textareaRef.current.blur()
      }
      if (this.props.quickactionRef.current) {
        this.props.quickactionRef.current.blur()
      }
      if (this.state.activeQA) {
        this.cancelQA().catch(handleError)
      }
    }
  }

  private cancelComposer = () => {
    this.setState({ qaComposerJson: undefined }, this.focusTextArea)
  }

  private cancelQA = () => {
    this.setState({ activeQA: undefined }, this.focusTextArea)
    return Promise.resolve(null)
  }

  private focusTextArea = () => {
    if (this.props.textareaRef.current) {
      this.props.textareaRef.current.focus()
    }
  }

  private finishedComposingQa = (qa: QuickActionResult) => {
    this.setState({ qaComposerJson: undefined })
    this.chooseQA(qa)
  }

  private chooseQA = (result: QuickActionResult) => {
    if (result.hasError) {
      return true // Cancel the choice
    } else if (result.composerJson) {
      this.setState({ qaComposerJson: result.composerJson })
    } else if (result.action) {
      if (result.body) {
        // There is both a message and addition non-message commands. Populate compose box, show <ActiveQADisplay/>
        this.setState(
          {
            activeQA: result,
            textEntered: result.body,
            warning: undefined,
          },
          this.focusTextArea
        )
      } else {
        const action = result.action
        // Action does not contain a message. Prompt user to confirm, then take action immediately.
        this.setState({ activeQA: result }, () => {
          this.confirmPostActionBound('Are you sure you want to ' + result.snippet + '?', action)
          this.setState({ activeQA: undefined })
        })
      }
    } else if (result.body) {
      // There is only a message. Just populate the compose box.
      this.setState({ textEntered: result.body }, this.focusTextArea)
    }
    return false
  }

  private changeText = (textEntered: string) => {
    this.setState({ textEntered })
  }

  /**
   * Select an arbitary range of text in the text area element for the user
   *
   * Useful for highlighting warnings, centering cursor in pre-filled text template.
   */
  private setSelectionRange(input: any, selectionStart: number, selectionEnd: number) {
    if (input.setSelectionRange) {
      input.focus()
      input.setSelectionRange(selectionStart, selectionEnd)
    } else if (input.createTextRange) {
      const range = input.createTextRange()
      range.collapse(true)
      range.moveEnd('character', selectionEnd)
      range.moveStart('character', selectionStart)
      range.select()
    }
  }

  /**
   * Set the cursor to a given index in any HTMLInputElement
   */
  private setCursorToIndex(textarea: any, pos: number) {
    this.setSelectionRange(textarea, pos, pos)
  }

  /**
   * Center the user's cursor in the existing pre-filled text.
   */
  private centerCursor = () => {
    this.focusTextArea()
    const textarea = this.props.textareaRef.current
    if (textarea) {
      // the index of the second '\n'
      const idxEmptySpace = textarea.placeholder.split('\n', 2).join('\n').length
      // place cursor right after
      this.setCursorToIndex(textarea, idxEmptySpace + 1)
    }
  }

  /**
   * Prefill textarea placeholder w helpful prompt
   */
  private maybePrefillPlaceholder = (botPreview: string | undefined) => {
    // threads with pending bot action show that as placeholder
    if (this.props.botThread && this.props.botThread.showCountdown && botPreview) {
      return botPreview
    }
    // agent and boss thread simple text template: "Hi ____, ... Best, Lisa"
    if ([ConversationType.AGENT, ConversationType.BOSS].includes(this.props.thread.conversationType)) {
      return this.prefillForBossOrAgent()
    }

    return 'Hi, Lisa here …' // default prompt for all other threads
  }

  /**
   * Prefill the textarea onClick with helpful text
   */
  private maybePrefillResponse = () => {
    // if the user hasn't entered any text yet, prefill:
    // "Hi ____, … Best, Lisa" for agents and bosses
    if (
      !this.state.textEntered &&
      [ConversationType.AGENT, ConversationType.BOSS].includes(this.props.thread.conversationType)
    ) {
      // set state doesn't happen immediately, so center as a callback
      this.setState({ textEntered: this.prefillForBossOrAgent() }, () => this.centerCursor())
    }
    // else: pending bot action if it exists
    return this.prefillQueuedBotResponseIfNecessary()
  }

  /**
   * Randomly returns one closing for the end of a prefilled msg
   */
  private randomClosing() {
    const closings = ['Best', 'Sincerely', 'Respectfully']
    return closings[randomInt(0, closings.length - 1)]
  }

  /**
   * It's faster for our Senior Ops to reply to agents and bosses with boilerplate copy
   */
  private prefillForBossOrAgent = () => {
    const firstName = this.props.thread.contactThem.firstName
    const greeting = 'Hi' + (firstName ? ' ' + firstName : '') + ','
    const goodbye = this.randomClosing() + ',\nLisa'
    return greeting + '\n\n\n\n' + goodbye
  }

  /**
   * If there is a queued bot response, populate that into textarea onClick
   */
  private prefillQueuedBotResponseIfNecessary = () => {
    if (this.state.activeQA || this.state.textEntered) {
      return
    }
    const action = this.props.botThread?.action
    if (!action) {
      return
    }
    const showCountdown = this.props.botThread?.showCountdown
    if (!showCountdown) {
      return
    }
    if (action.commandJsons.find(c => c.includes('evaluation skipped until send time'))) {
      return // don't prefill placeholder actions
    }
    if (!action.commandJsons.find(c => c.includes('SEND_MESSAGE'))) {
      return // don't prefill actions that don't contain a SEND_MESSAGE
    }

    const displayName = action.classification ? formatEnum(action.classification) : 'Queued Response'

    const res: QuickActionResult = {
      __typename: 'QuickActionResult',
      displayName,
      snippet: '',
      body: action.preview,
      hasError: false,
      action,
      composerJson: null,
    }

    this.setState({
      activeQA: res,
      textEntered: res.body!,
    })
  }

  private onSubmitBound = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
  }

  private nameCheckRegexes = [
    // too many false positives: /^[\w-]+,/, // alice, ...
    /^hi [\w-]+[,.!]?/, // hi alice(,|.|!)?
    /^hey [\w-]+[,.!]?/, // hey alice(,|.|!)?
    /^good (morning|afternoon|evening) [\w-]+[,.!]/, // good (morning|afternoon|evening) alice(,|.|!)
    /thanks [\w-]+[,!.]/, // thanks alice(,|.|!)
    /thank you [\w-]+[,!.]/, // thank you alice(,|.|!)
  ]

  private checkWarningBeforeSend = () => {
    const { warning } = this.state

    // don't double warn unless its a CREATE_NOTE warning
    if (warning && warning !== this.CREATE_NOTE_VALIDATION_ERROR_MESSAGE) {
      return false
    }

    const newWarning = this.computeWarning()
    if (newWarning != null) {
      this.setState({ warning: newWarning })
      return true
    }
    return false
  }

  private checkIfWrongName(text: string, contactName?: string | null) {
    // check whether we're about to call someone the wrong name
    if (contactName) {
      const normalizedContact = contactName.toLowerCase()
      for (const regex of this.nameCheckRegexes) {
        const match = regex.exec(text)
        if (match) {
          const matchedText = match[0]
          if (!matchedText.includes(normalizedContact)) {
            return true
          }
        }
      }
    }
    return false
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private computeWarning() {
    const { thread } = this.props
    const { textEntered, activeQA } = this.state
    const normalText = textEntered.toLowerCase().replace('\n', ' ')

    const unreplacedVar = this.checkForUnreplacedVars(normalText)
    if (unreplacedVar) {
      return unreplacedVar
    }

    const contactName = thread.contactThem.firstName
    if (this.checkIfWrongName(normalText, contactName)) {
      return 'Did you mean to type ' + contactName + '?'
    }

    const longUrl = this.checkLongUrl(textEntered)
    if (longUrl) {
      return longUrl
    }

    const lisaSignoff = this.checkLisaSignoff(normalText)
    if (lisaSignoff) {
      return lisaSignoff
    }

    // check if the Operator is about to repeat themselves
    if (alreadySentInThread(textEntered, thread)) {
      return 'Check for repetition?'
    }

    // allow dynasty to appear in a link (e.g. for our how-to gifs)
    if (
      [ConversationType.PROSPECT, ConversationType.RESIDENT].includes(thread.conversationType) &&
      (normalText.includes(' dynasty ') || normalText.includes(' dynasty') || normalText.includes('dynasty '))
    ) {
      return `Never say 'Dynasty' when communicating with a ${thread.conversationType.toLowerCase()}`
    }

    const castedMisspellings = misspellings as { [key: string]: string[] }
    const words = normalText.split(' ')
    for (const word of words) {
      // check for common misspellings
      if (castedMisspellings[word]) {
        return `Did you mean \"${castedMisspellings[word].join('" or "')}\"?`
      }
    }

    // stop from sending Agent/Boss message when note can't be parsed
    const createNoteValidationError = activeQA ? this.checkCreateNoteValidation(textEntered, activeQA) : undefined
    if (createNoteValidationError) {
      return createNoteValidationError
    }

    return null
  }

  private checkForUnreplacedVars(normalText: string) {
    const unreplacedVar = new RegExp('{{[A-Za-z0-9]*}}').exec(normalText)
    if (unreplacedVar) {
      return 'You forgot to fill in ' + unreplacedVar[0]
    }

    return undefined
  }

  private checkLongUrl(textEntered: string) {
    // https://stackoverflow.com/a/3809435
    const re = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
    let match
    while ((match = re.exec(textEntered)) != null) {
      if (match[0].length >= 160) {
        return 'The URL in your message is too long, please shorten it.'
      }
    }

    return undefined
  }

  private checkLisaSignoff(normalText: string) {
    // check if the user accidentally signed off as themselves, not Lisa
    const tokens: string[] = this.props.user.name.trim().split(' ')
    const lastWord = normalText.split(' ').pop()!.trim()
    if (tokens) {
      for (const t of tokens) {
        if (lastWord.includes(t.toLowerCase())) {
          return 'Did you mean “Lisa,” not “' + t + '”?'
        }
      }
    }

    return undefined
  }

  private checkCreateNoteValidation(textEntered: string, activeQA: QuickActionResult) {
    const message = textEntered.trim()

    const isBodyEdited = activeQA.body !== message
    if (isBodyEdited && activeQA.action && activeQA.action.actionName === ActionName.CREATE_NOTE) {
      /**
       * In order to send a message and create a new note, the message body must match the message template created by the {@code BossAgentQuickActions::createNoteQA}.
       * To validate that there are no missing parts or excess quotation marks, we find all substrings between the quotation marks.
       *
       * Substring 1: OK, sure thing, I can do that. I'll add a note from you in my memory for {firm/task/agent/boss}:
       * Substring 2: "{noteBody}"
       * Substring 3: — {agent or boss name} on {date}
       */
      const subStrings = message.match(new RegExp('([^"]+)', 'gm')) || []

      // also need to count how many qutation marks exist in the string since an empty quote will not result in a substring match
      const numOfQuotationMarks = (message.match(new RegExp('"', 'g')) || []).length
      if (subStrings.length !== 3 || numOfQuotationMarks !== 2) {
        return this.CREATE_NOTE_VALIDATION_ERROR_MESSAGE
      }
    }

    return undefined
  }
}

/**
 * Check if a text string has been said before in this thread.
 */
function alreadySentInThread(textEntered: string, thread: FetchThreadQuery['thread']): boolean {
  if (thread.messagesWithLinks.messages.length === 0 || textEntered.trim().split(' ').length <= 5) {
    // if the thread is new or the string is <5 words, let it pass
    return false
  }
  return thread.messagesWithLinks.messages.some(m => !m.isInbound && m.censoredShortBody.includes(textEntered))
}

class ActiveQADisplay extends React.PureComponent<{
  qa: QuickActionResult
  onCancel: () => void
}> {
  render() {
    const { qa, onCancel } = this.props
    return (
      <ActiveQADisplayWrap>
        <Pellet>{qa.displayName}</Pellet>
        <Button onClick={onCancel} size="medium">
          ×
        </Button>
      </ActiveQADisplayWrap>
    )
  }
}

const ResetReplyTo = styled(Link)`
  position: absolute;
  top: 0;
  right: 0;
  user-select: none;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin-right: 16px;
  &:hover {
    color: white;
  }
`

const ReplyingTo = styled(BodyTextSpan)`
  color: ${Colors.midway};
  padding-right: 8px;
`

const OriginalMessage = styled(BodyText)`
  padding: 16px;
  color: white;
  overflow: hidden;
  color: ${Colors.silver};
  cursor: pointer;
  border: 1px solid ${Colors.charcoal};
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const OriginalMessageText = styled(BodyTextSpan)`
  max-width: calc(100% - 16px);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
`

const ActiveQADisplayWrap = styled(IntroText)`
  display: flex;
  justify-content: space-between;
  min-height: 36px;
  margin-bottom: 4px;
`

const Pellet = styled(IntroText)`
  display: block;
  padding: 5px 12px;
  border-radius: 4px;
  border: 1px solid ${Colors.silver};
`

const SendContainer = styled.div`
  margin-top: 32px;
`

const SendForm = styled.form`
  display: flex;
  flex-direction: column;
`

const SendButtonRow = styled.div`
  display: flex;
  flex-direction: row;
`

const SentButtonRowDetails = styled(BodyText)`
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
`

const ButtonWithLeftMargin = styled(Button)`
  white-space: nowrap;
  margin-left: 16px;
`

const TypingIndicatorText = styled(SmallerText)`
  min-height: 16px;
`

export default function SendMessageBox(props: SendMessageBoxProps) {
  const [mutate] = useMutation<SendMessageMutation, SendMessageMutationVariables>(sendMessageMutation)
  return <SendMessageBoxImpl {...props} mutate={mutate} />
}

interface SendButtonProps {
  onClick?: () => void
  disabled?: boolean
  action?: ActionToExecFragment | null
  hideArchiveThread?: boolean
}

/**
 * Append `& Snooze`, `& Archive` or nothing to the button body based on {@link SendButtonProps#action}.
 */
export class SendButton extends React.PureComponent<SendButtonProps> {
  render() {
    const { onClick, disabled, action, children, hideArchiveThread } = this.props
    let additionalBody: string | undefined
    if (!hideArchiveThread) {
      if (action?.hasSnoozeCommand) {
        additionalBody = ' & Snooze'
      } else if (action?.hasArchiveCommand && action.actionName !== 'OUTSIDE_SCHEDULING') {
        // TECH DEBT: we only want to show & Archive if the thread we're currently on is the one that's
        // going to be archived. This logic says "append '& Archive' if the action has any 'set archived = true' cmds."
        additionalBody = ' & Archive'
      }
    }

    return (
      <ButtonWithLeftMargin color="sky" onClick={onClick} disabled={disabled}>
        {children}
        {additionalBody}
      </ButtonWithLeftMargin>
    )
  }
}
